import type { TFunction } from 'i18next';

import type { ISelectOption } from '@feathr/components';

/** Expiration presets for a service key. */
export enum EExpirationPreset {
  SevenDays = '7',
  ThirtyDays = '30',
  SixtyDays = '60',
  NinetyDays = '90',
  Custom = 'custom',
  NoExpiration = 'none',
}

export interface IExpirationOption extends ISelectOption {
  id: EExpirationPreset;
  name: string;
}

/** Number of days each numeric preset adds to the reference date. */
const PRESET_DAYS: Partial<Record<EExpirationPreset, number>> = {
  [EExpirationPreset.SevenDays]: 7,
  [EExpirationPreset.ThirtyDays]: 30,
  [EExpirationPreset.SixtyDays]: 60,
  [EExpirationPreset.NinetyDays]: 90,
};

/** ISO 8601 `expires_at` for a numeric preset; `undefined` for Custom/NoExpiration. */
export function getExpiresAt(preset: EExpirationPreset, reference: Date): string | undefined {
  const days = PRESET_DAYS[preset];
  if (days === undefined) {
    return undefined;
  }
  const expires = new Date(reference.getTime());
  expires.setUTCDate(expires.getUTCDate() + days);
  return expires.toISOString();
}

/** ISO 8601 `expires_at` for a picked date, normalized to UTC midnight. */
export function getExpiresAtForDate(date: Date): string {
  const expires = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  return expires.toISOString();
}

/** Human-readable `YYYY-MM-DD` slice of an ISO 8601 datetime for inline labels. */
function formatExpiryDate(isoDatetime: string): string {
  return isoDatetime.slice(0, 10);
}

/** Select options: numeric presets with the computed date inline, plus Custom and No expiration. */
export function getExpirationOptions(t: TFunction, reference: Date): IExpirationOption[] {
  const numeric: EExpirationPreset[] = [
    EExpirationPreset.SevenDays,
    EExpirationPreset.ThirtyDays,
    EExpirationPreset.SixtyDays,
    EExpirationPreset.NinetyDays,
  ];

  const options: IExpirationOption[] = numeric.map((preset) => {
    const expiresAt = getExpiresAt(preset, reference)!;
    return {
      id: preset,
      name: t('{{days}} days ({{date}})', {
        date: formatExpiryDate(expiresAt),
        days: PRESET_DAYS[preset],
      }),
    };
  });

  options.push({ id: EExpirationPreset.Custom, name: t('Custom date') });
  options.push({ id: EExpirationPreset.NoExpiration, name: t('No expiration') });

  return options;
}
